import {useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useUserContext} from '@containers/App/UserProvider'
import type {UserContextProps} from '@containers/App/UserProvider'
import type {ServiceCapabilities, UserInterface, ViewAsStateInterface} from '@interfaces/user'
import {useIsReadOnly} from '@hooks/useIsReadOnly'
import {useServiceCapabilities} from '@hooks/useServiceCapabilities'

interface CurrentUserHookInterface {
  isLineAdmin: boolean
  isSubscribedToLineConnectivity: boolean
  isNotificationsDisabled: boolean
  isAccountAdmin: boolean
  isSubscriptionAdmin: boolean
  canManageUsers: boolean
  canManageRoles: boolean
  canManageTeams: boolean
  canManageNotificationSubscriptions: boolean
  currentUser: UserInterface | null
  hasAgreedToTerms: boolean
  hasAgreedToPrivacyPolicy: boolean
  // A read-only platform admin may VIEW every admin area (the canView* flags
  // below OR it in); the is*Admin / canManage* flags above stay as the
  // management (edit) capabilities. isReadOnly hides edit controls.
  isReadOnly: boolean
  canViewMachineAdmin: boolean
  canViewAccountManagement: boolean
  canViewUsers: boolean
  canViewRoles: boolean
  canViewTeams: boolean
  canViewNotificationSubscriptions: boolean
  // "View As": whether the viewer may start an impersonation session, and the
  // currently-active overlay (active=false when not viewing as anyone).
  /** The user's own IANA zone from their profile, '' when they have not set one. */
  profileTimezone: string
  canViewAs: boolean
  viewAs: ViewAsStateInterface
  // Per-service read/write capability (AI / Edge / Anomaly Detection) used to
  // hide product areas and disable configuration a user cannot access.
  serviceCapabilities: ServiceCapabilities
}

export const useCurrentUser = (): CurrentUserHookInterface => {
  const [searchParams] = useSearchParams()
  const notificationType = searchParams.get('notification_type')
  // Tolerate a missing UserProvider (e.g. the public Alpinist demo embed renders a minimal
  // provider stack) — same guard as useServiceCapabilities.
  const {currentUser} = (useUserContext() as UserContextProps | null) ?? {currentUser: null}
  const [isSubscribedToLineConnectivity, setIsSubscribedToLineConnectivity] =
    useState<boolean>(false)
  const [isLineAdmin, setIsLineAdmin] = useState<boolean>(false)
  const [isAccountAdmin, setIsAccountAdmin] = useState<boolean>(false)
  const [canManageUsers, setCanManageUsers] = useState<boolean>(false)
  const [canManageRoles, setCanManageRoles] = useState<boolean>(false)
  const [canManageTeams, setCanManageTeams] = useState<boolean>(false)
  const [canManageNotificationSubscriptions, setCanManageNotificationSubscriptions] =
    useState<boolean>(false)
  const [isSubscriptionAdmin, setIsSubscriptionAdmin] = useState<boolean>(false)
  const [isNotificationsDisabled, setIsNotificationsDisabled] = useState<boolean>(false)
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState<boolean>(false)
  const [hasAgreedToPrivacyPolicy, setHasAgreedToPrivacyPolicy] = useState<boolean>(false)

  const isReadOnly = useIsReadOnly()
  // Single source of truth for per-service capabilities (also usable standalone
  // via useServiceCapabilities for consumers that don't need the rest of this hook).
  const serviceCapabilities = useServiceCapabilities()

  useEffect(() => {
    if (currentUser && currentUser.userprofile) {
      const profile = currentUser.userprofile
      // Admin capabilities now come from the tenants service flags; fall back to
      // the legacy pm-core role names for older payloads.
      const lineAdmin = profile.is_line_admin === true || profile.roles.includes('Machine Admin')
      const accountAdmin =
        profile.is_account_admin === true || profile.roles.includes('Account Admin')
      const subscriptionAdmin =
        profile.is_subscription_admin === true || profile.roles.includes('Subscription Admin')

      setIsSubscribedToLineConnectivity(
        Boolean(profile.notification_preference_machine_connectivity_is_subscribed),
      )
      setIsLineAdmin(lineAdmin)
      setIsAccountAdmin(accountAdmin)
      // Per-category Account Management capabilities (users/roles/teams). Older
      // payloads without these flags fall back to the overall account-admin flag.
      setCanManageUsers(profile.can_manage_users ?? accountAdmin)
      setCanManageRoles(profile.can_manage_roles ?? accountAdmin)
      setCanManageTeams(profile.can_manage_teams ?? accountAdmin)
      setCanManageNotificationSubscriptions(
        profile.can_manage_notification_subscriptions ?? accountAdmin,
      )
      setIsSubscriptionAdmin(subscriptionAdmin)
      setIsNotificationsDisabled(
        (notificationType === 'machine' && !lineAdmin) ||
          (notificationType === 'admin' && !accountAdmin),
      )
      setHasAgreedToPrivacyPolicy(profile.has_agreed_to_privacy_policy)
      setHasAgreedToTerms(profile.has_agreed_to_terms_and_conditions)
    }
  }, [currentUser, notificationType])

  return {
    isLineAdmin,
    isSubscribedToLineConnectivity,
    isNotificationsDisabled,
    isAccountAdmin,
    isSubscriptionAdmin,
    canManageUsers,
    canManageRoles,
    canManageTeams,
    canManageNotificationSubscriptions,
    currentUser,
    hasAgreedToTerms,
    hasAgreedToPrivacyPolicy,
    isReadOnly,
    // A read-only platform admin can view a section even without the management
    // capability. (Superusers already have the management flags set, so they
    // pass these checks the same way an owner/admin does.)
    canViewMachineAdmin: isLineAdmin || isReadOnly,
    canViewAccountManagement: isAccountAdmin || isReadOnly,
    canViewUsers: canManageUsers || isReadOnly,
    canViewRoles: canManageRoles || isReadOnly,
    canViewTeams: canManageTeams || isReadOnly,
    canViewNotificationSubscriptions: canManageNotificationSubscriptions || isReadOnly,
    // The user's PROFILE zone, not the browser's. DashTemplate seeds the profile
    // from the browser once, on first load, so this is the zone the user arrived
    // with unless they have since changed it in Settings -- and a change there
    // then follows them onto every device (AB#5875).
    profileTimezone: currentUser?.userprofile?.preferred_timezone ?? '',
    canViewAs: currentUser?.userprofile?.can_view_as === true,
    viewAs: currentUser?.userprofile?.view_as ?? {active: false},
    serviceCapabilities,
  }
}
